/**
 * Account Search Feature Hook
 *
 * Manages search state and executes search queries.
 * Encapsulates search/filter/pagination logic.
 */

import { useState, useEffect } from 'react';
import { useSearchAccounts, type Platform, type SearchSnsAccountsData } from '@/entities/account';
import type { AccountSearchState, AccountSearchData } from './types';

export const useAccountSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [platform, setPlatform] = useState<Platform | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(0); // 0-based
  const [pageSize, setPageSize] = useState(10);

  // Build search parameters
  const searchParams: SearchSnsAccountsData['query'] = {
    page: currentPage,
    size: pageSize,
    sortBy: 'createdAt',
    sortDirection: 'desc',
    ...(searchTerm.trim() && { accountId: searchTerm.trim() }),
    ...(platform !== 'all' && { platform }),
  };

  // Execute search query
  const { data: response, isLoading, error } = useSearchAccounts(searchParams);

  // Extract data from response
  const pageData = response?.data;
  const accounts = pageData?.content || [];
  const totalPages = pageData?.total_pages || 1;
  const totalElements = pageData?.total_elements || 0;

  // Reset pagination when search conditions change
  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, platform]);

  const resetPagination = () => {
    setCurrentPage(0);
  };

  // State interface
  const state: AccountSearchState = {
    searchTerm,
    platform,
    currentPage,
    pageSize,
    setSearchTerm,
    setPlatform,
    setCurrentPage,
    setPageSize,
    resetPagination,
  };

  // Data interface
  const data: AccountSearchData = {
    accounts,
    totalPages,
    totalElements,
    isLoading,
    error: error as Error | null,
  };

  return {
    state,
    data,
  };
};
